/**
 * The translation engine: a dot-path resolver over a chain of catalogues, plus the two things
 * the marketing site's version does not have — `{name}` interpolation and `Intl.PluralRules`
 * plurals (POS2-1133).
 *
 * Ported from `marketing/src/i18n/utils.ts`, with one deliberate difference: the glob below is
 * **not** eager. Astro builds one HTML page per locale, so an eager glob tree-shakes per page;
 * a single-page app would bundle all ten locales into its main chunk. Here every non-English
 * locale is its own lazily-fetched chunk, and `en` is a static import — the floor that
 * guarantees the app never renders a raw key at first paint or offline.
 */
import { defaultLocale } from "./config";

export type NestedMessages = { [key: string]: string | NestedMessages };

/** One locale's messages, keyed by namespace: `{ nav: {...}, common: {...} }`. */
export type Catalog = Record<string, NestedMessages>;

export type TParams = Record<string, string | number>;
export type TFunction = (key: string, params?: TParams) => string;

/**
 * The bundled English catalogue. Eager, so Vite resolves it to static imports and it sits in the
 * entry chunk — always available, offline included, which is what makes English the guaranteed
 * floor of every fallback chain.
 *
 * A glob rather than one import per namespace (POS2-1134 CP2). The hand-written list was a trap
 * with no gate behind it: adding `messages/en/auth.json` and `messages/de/auth.json` passes
 * `check-i18n.mjs` — which reads the directory, not this module — and every screen using the new
 * namespace then renders raw keys, because `translate` never sees it. Found by a component test
 * that read the German sign-in screen; nothing else in the toolchain could see it.
 */
const enModules = import.meta.glob<NestedMessages>("./messages/en/*.json", {
  eager: true,
  import: "default",
});

export const enCatalog: Catalog = Object.fromEntries(
  Object.entries(enModules).map(([path, messages]) => [
    path.slice("./messages/en/".length).replace(/\.json$/, ""),
    messages,
  ]),
);

// English is excluded from the glob because it is statically imported above. Left in, Rollup
// warns on every build that the dynamic import cannot move a statically-imported module into its
// own chunk — which is the intended behaviour, so the honest fix is to stop asking for it.
const loaders = import.meta.glob<NestedMessages>(["./messages/*/*.json", "!./messages/en/*.json"], {
  import: "default",
});
const cache = new Map<string, Catalog>();

/** Every namespace file for one locale, or `null` if the locale ships no catalogue at all.
 *
 * Exported for the admin translation table, which needs one locale's bundle on its own rather
 * than a resolution chain: it shows the English source and the locale's value side by side, and
 * has to distinguish "translated" from "identical to English".
 */
export async function loadCatalog(locale: string): Promise<Catalog | null> {
  const cached = cache.get(locale);
  if (cached) return cached;

  const prefix = `./messages/${locale}/`;
  // Dotfiles excluded explicitly rather than trusting the glob: `.source-hashes.json` sits in
  // this directory and ends in `.json`, and loading it would invent a `.source-hashes` namespace
  // full of sha256 digests (POS2-1133).
  const files = Object.entries(loaders).filter(
    ([path]) => path.startsWith(prefix) && !path.slice(prefix.length).startsWith("."),
  );
  if (files.length === 0) return null;

  const loaded = await Promise.all(
    files.map(async ([path, load]) => {
      const namespace = path.slice(prefix.length).replace(/\.json$/, "");
      return [namespace, await load()] as const;
    }),
  );

  const catalog = Object.fromEntries(loaded);
  cache.set(locale, catalog);
  return catalog;
}

/**
 * The bundled catalogues to consult for a locale, highest priority first, with English last.
 *
 * A regional variant gets its base locale in between — `en-US` falls through to `en`, `de-AT`
 * to `de` — which is the rung that makes shipping a variant a translation of the differences
 * only. CP3 prepends the database override layer to this chain.
 */
export async function loadChain(locale: string): Promise<Catalog[]> {
  const base = locale.split("-")[0];
  const wanted = locale === base ? [locale] : [locale, base];
  const chain: Catalog[] = [];

  for (const candidate of wanted) {
    if (candidate === defaultLocale) continue;
    const catalog = await loadCatalog(candidate);
    if (catalog) chain.push(catalog);
  }

  chain.push(enCatalog);
  return chain;
}

/** `{ "nav.stock": "Stock", … }` — a catalogue as a flat key→value map, for the admin table. */
export function flattenCatalog(catalog: Catalog): Record<string, string> {
  const flat: Record<string, string> = {};
  const walk = (node: NestedMessages, prefix: string) => {
    for (const [key, value] of Object.entries(node)) {
      const path = `${prefix}.${key}`;
      if (typeof value === "string") flat[path] = value;
      else walk(value, path);
    }
  };
  for (const [namespace, messages] of Object.entries(catalog)) walk(messages, namespace);
  return flat;
}

/** The `{placeholders}` a string carries, as a sorted set — what an edit must not change. */
export function placeholdersOf(value: string): string[] {
  return [...new Set(value.match(/\{(\w+)\}/g) ?? [])].sort();
}

/**
 * Does an edited translation carry the same placeholders as the value it replaces?
 *
 * The *set*, not the sequence: German word order routinely moves a placeholder, and reordering is
 * exactly what a translator is supposed to be free to do. Dropping or renaming one is not — the
 * runtime would render the string with the value missing, or with a literal `{Name}` on screen.
 *
 * Separate from the admin page so both directions can be tested without depending on which keys
 * the shipped catalogue happens to contain.
 */
export function placeholdersMatch(reference: string, value: string): boolean {
  return placeholdersOf(reference).join() === placeholdersOf(value).join();
}

/** Walk `ns.a.b` through one catalogue. Undefined if any segment is missing or not a string. */
function lookup(catalog: Catalog, key: string): string | undefined {
  const [namespace, ...path] = key.split(".");
  let current: string | NestedMessages | undefined = catalog[namespace];

  for (const segment of path) {
    if (typeof current !== "object" || current === null) return undefined;
    current = current[segment];
  }

  return typeof current === "string" ? current : undefined;
}

/**
 * `{name}` → `params.name`. A placeholder with no matching param is left as-is: a visible
 * `{name}` in the UI is a bug report, where an empty string is a silent one.
 */
function interpolate(template: string, params: TParams): string {
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in params ? String(params[name]) : match,
  );
}

/**
 * The keys to try for one call, in order. A `count` param turns `example.items` into
 * `example.items_one` / `example.items_other` — `Intl.PluralRules` picks the category, and locales
 * with more of them (`_few`, `_many`) need no code change, only more keys.
 *
 * The category is selected for the *active* locale even when the string ends up coming from a
 * fallback catalogue. English and German share one/other, so the two can only diverge for a
 * locale that does not ship yet.
 */
function candidates(locale: string, key: string, params: TParams | undefined): string[] {
  if (typeof params?.count !== "number") return [key];
  const category = new Intl.PluralRules(locale).select(params.count);
  return category === "other" ? [`${key}_other`, key] : [`${key}_${category}`, `${key}_other`, key];
}

/**
 * Resolve a key against the chain and fill in its params.
 *
 * Catalogue-major, not candidate-major: a German `_other` beats an English `_one`, because a
 * partially-pluralised German string is still German. The last resort is the key itself, which
 * is what CP2's CI check exists to make impossible in a shipped build.
 */
export function translate(
  chain: readonly Catalog[],
  locale: string,
  key: string,
  params?: TParams,
): string {
  const keys = candidates(locale, key, params);

  for (const catalog of chain) {
    for (const candidate of keys) {
      const value = lookup(catalog, candidate);
      if (value !== undefined) return params ? interpolate(value, params) : value;
    }
  }

  return key;
}
